/**
 * Right above "הכנת חוזה": why the button waits for the documents the role row sends
 * (`roleDocumentsState`). One still loading reads "טוענים את חוזה הבלעדיות…" (or the contract, the
 * contracts, the questionnaire); one that could not be loaded says so, with "נסו שוב" and, for the
 * exclusivity order, "המשך בלי בלעדיות", which unticks the box: the order alone, as the realtor's
 * own choice, never by default.
 *
 * A wait shorter than LOADING_NOTE_DELAY_MS shows nothing. Every pick passes through one render
 * before the role effect applies its documents, and a warm session applies them in that very pass.
 * After a failure, "נסו שוב" shows the wait at once.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Stack, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { roleDocumentsBlockKey } from "./roleDocuments";
import type { RoleDocumentsBlock } from "./roleDocuments";

export const LOADING_NOTE_DELAY_MS = 300;

const PANEL_SX = { flexShrink: 0, borderTop: 1, borderColor: "divider", px: { xs: 2, md: 3 }, py: 1.25 } as const;

export function RoleDocumentsNote({
  block,
  onRetry,
  onWithoutExclusivity
}: {
  block: RoleDocumentsBlock | null;
  /** Fetch the role's documents again. */
  onRetry: () => void;
  /** Untick "החתמה בבלעדיות". */
  onWithoutExclusivity: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const state = block?.state ?? null;
  const [loadingShown, setLoadingShown] = useState(false);
  useEffect(() => {
    // After a failure the next wait is a "נסו שוב": shown at once.
    if (state === "failed") {
      setLoadingShown(true);
      return undefined;
    }
    if (state !== "loading") {
      setLoadingShown(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setLoadingShown(true), LOADING_NOTE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  if (!block || (block.state === "loading" && !loadingShown)) return null;
  if (block.state === "loading") {
    return (
      <Box
        role="status"
        aria-live="polite"
        data-testid="role-documents-loading"
        sx={{ ...PANEL_SX, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0 }} />
          <Typography variant="body2">{t(roleDocumentsBlockKey(block))}</Typography>
        </Stack>
      </Box>
    );
  }
  const exclusivity = block.subject === "exclusivity";
  return (
    <Box
      role="alert"
      data-testid="role-documents-failed"
      sx={{ ...PANEL_SX, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14) }}
    >
      <Typography variant="subtitle2" component="p">
        {t(roleDocumentsBlockKey(block))}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {t(exclusivity ? "signatures.newSignatureForm.roleDocuments.failedHelpExclusivity" : "signatures.newSignatureForm.roleDocuments.failedHelp")}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" variant="contained" onClick={onRetry}>
          {t("signatures.newSignatureForm.roleDocuments.retry")}
        </Button>
        {exclusivity && (
          <Button size="small" variant="outlined" onClick={onWithoutExclusivity}>
            {t("signatures.newSignatureForm.roleDocuments.withoutExclusivity")}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The role row's documents list while none of the role's documents is selected: a spinner while
 * they load and, once they failed, the failure in words. Never a spinner over a failure.
 */
export function RoleDocumentsListPlaceholder({ block }: { block: RoleDocumentsBlock | null }): JSX.Element {
  const { t } = useTranslation();
  if (block && block.state === "failed") {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="role-documents-list-failed">
        {t(roleDocumentsBlockKey(block))}
      </Typography>
    );
  }
  return <CircularProgress size={20} />;
}
